import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Text, Button } from '@chakra-ui/react';

export interface ExpandableClampTextProps {
  text: string;
  /** Lines shown when collapsed (default 2). */
  lines?: number;
  fontSize?: string;
  fontWeight?: string;
  color?: string;
  lineHeight?: string;
  /** Margin below block (including toggle). */
  mb?: string;
  moreLabel?: string;
  lessLabel?: string;
}

/**
 * Clamps text to N lines; shows a "More" control when overflow is detected (resize-safe).
 */
export function ExpandableClampText({
  text,
  lines = 2,
  fontSize = 'xs',
  fontWeight = 'normal',
  color = 'volyx.textSecondary',
  lineHeight,
  mb = '0',
  moreLabel = 'More',
  lessLabel = 'Less',
}: ExpandableClampTextProps) {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowsWhenCollapsed, setOverflowsWhenCollapsed] = useState(false);

  useLayoutEffect(() => {
    setExpanded(false);
    setOverflowsWhenCollapsed(false);
  }, [text, lines]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      if (expanded) return;
      setOverflowsWhenCollapsed(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [text, lines, expanded]);

  const showToggle = overflowsWhenCollapsed;

  return (
    <Box mb={mb}>
      <Text
        ref={ref}
        fontSize={fontSize}
        fontWeight={fontWeight}
        color={color}
        lineHeight={lineHeight}
        m="0"
        lineClamp={expanded ? undefined : lines}
        overflowWrap="anywhere"
        wordBreak="break-word"
        whiteSpace="pre-wrap"
      >
        {text}
      </Text>
      {showToggle ? (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          mt="1"
          px="0"
          py="0"
          h="auto"
          minH="0"
          fontWeight="semibold"
          color="volyx.action"
          _hover={{ bg: 'transparent', color: '#0063CC' }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? lessLabel : moreLabel}
        </Button>
      ) : null}
    </Box>
  );
}
